import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { FullCalendarModule } from '@fullcalendar/angular';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, DatesSetArg, EventClickArg } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import { DateTime } from 'luxon';
import { CalendarService } from './services/calendar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Sequences } from '@shared/models/activity-summary/sequences';
import { SkeletonModule } from 'primeng/skeleton';
import { tap } from 'rxjs';
import { FlightDetailDialogComponent } from '../flight-detail/flight-detail-dialog/flight-detail-dialog.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FlightTotalsService } from '../services/flight-totals.service';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';
import { AppSettings } from '@shared/models/settings/app-settings';


@Component({
    selector: 'app-flights-calendar',
    imports: [
        CommonModule,
        FullCalendarModule,
        SkeletonModule
    ],
    templateUrl: './flights-calendar.component.html',
    styleUrl: './flights-calendar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlightsCalendarComponent implements OnInit {
  @ViewChild('calendar') calendar?: FullCalendarComponent;
  ref: DynamicDialogRef | undefined;
  lastUpdated?: string;
  specifiedEmpNum = '';
  isHolidayPayMonth = false;
  appSettings?: AppSettings;

  calendar$ = this.calendarService.calendar$.pipe(
    tap( (calendar) => {
      const timestamp = calendar.response?.timestamp ? calendar.response.timestamp.toISO() : '';
      this.lastUpdated = this.calendarService.loadLastUpdted(timestamp || '');
    })
  );  
  
  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    height: 'auto',
    handleWindowResize: true,
    unselectAuto: false,
    fixedWeekCount: false,
    showNonCurrentDates: true,
    titleFormat: {
      month: 'long',
      year: 'numeric',
    },
    headerToolbar: {
      start: 'prev, prevYear',
      center: 'title',
      end: 'nextYear, next' 
    },
    datesSet: this.onMonthChanged.bind(this),
    eventClick: this.onEventClick.bind(this),
  };


  constructor(
    private calendarService: CalendarService,
    private router: Router,
    private dialogService: DialogService,
    private flightTotalsService: FlightTotalsService,
    private route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    
    this.appSettings = JSON.parse(localStorage.getItem('appSettings') || '');

    //update breadcrumb service
    const url = location.pathname;
    const breadcrumbLabel = this.route.snapshot.data['breadcrumb'] || 'Calendar';
    this.breadcrumbService.addToBreadcrumbs(url, breadcrumbLabel);
    this.breadcrumbService.removeBreadcrumb(['Sequence', 'Flight', 'Flights']);


    const initializedDate = this.calendarService.getIntialDate();
    const initialDate = this.calendarService.createDateStringFrom(initializedDate);
    const date = DateTime.fromISO(initialDate).plus({ days: 15 });
    this.setHolidayPayMonth( { year: date.year, period: date.month });
    this.calendarService.setSelectedMonth(initializedDate); 
    this.calendarOptions = {
      ...this.calendarOptions,
      initialDate     
    }

  }

  onEventClick(event: EventClickArg): void {
    const sequence = this.appSettings?.sortOrder === 1 ? 
    this.reversedLegsForSequence(event.event.extendedProps['sequence']) :
      event.event.extendedProps['sequence'];
    const sequenceNum = sequence.sequenceNumber;
    const originationDate = DateTime.fromISO(
      sequence.originationDate, { zone: 'utc' }
    ).toFormat('yyyy-MM-dd');

    this.router.navigate(['sequence', sequenceNum, 'FO', originationDate]);
  }

  reversedLegsForSequence(sequence: Sequences): Sequences {
    sequence;
    for ( const duty of sequence.dutyPeriodSummaries.reverse()) {
      duty.legs.reverse();
    }
    return sequence;
  }

  // Updates selectedMonth observable triggering events update.
  onMonthChanged(dateRange: DatesSetArg): void {
    const date = DateTime.fromISO(dateRange.startStr).plus({ days: 15 });
    const dateObject = {
      year: date.year,
      period: date.month
    }
    this.calendarService.setSelectedMonth(dateObject);
    this.setHolidayPayMonth(dateObject);
    

    if (this.calendar) {
      const api = this.calendar.getApi();
      api.getEvents();
    }
  }

  onHelpClicked(): void {
    this.ref = this.dialogService.open(FlightDetailDialogComponent, {
          data: 3,
          header: 'Calendar',
          width: '50vw',
          modal: true,
          dismissableMask: true,
          closable: true,
          breakpoints: {
            '500px': '98vw',
          },
          contentStyle: { 
            'background-color': 'var(--surface-0)',
          }
        });
  }

  onRefreshClicked(): void {
   this.calendarService.forceRefresh();
   this.flightTotalsService._isRefresh.next(true);
  }

  setHolidayPayMonth(dateObject: { year: number; period: number }): void {
    // Set isHolidayPayMonth flag.
    const month = dateObject.period;
    if (
      month === 1 ||
      month === 5 ||
      month === 7 ||
      month === 11 ||
      month === 12
    ) {
      this.isHolidayPayMonth = true;
    } 
    else {
      this.isHolidayPayMonth = false;
    }
  }
}
